import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2, Upload, Lock } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Notice, Empty, ProvenanceBadge } from "@/components/Badges";
import { DOC_CATEGORIES, fmtDateTime } from "@/lib/format";

const Facts = ({ doc, reload }) => {
  const apply = async (fid) => {
    try {
      await api.post(`/documents/${doc.id}/apply-fact`, { fact_id: fid });
      toast.success("Applied to profile - labelled as extracted from document");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!doc.facts?.length) return null;
  return (
    <div className="mt-3 space-y-2 rounded-md bg-blue-50/50 p-3">
      <p className="text-xs font-medium text-blue-900">Facts found in this document - confirm before use</p>
      {doc.facts.map((f) => (
        <div key={f.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span><ProvenanceBadge source="document" /> <b>{f.label}:</b> {f.value} <span className="text-slate-500">“…{f.snippet.slice(0, 90)}…”</span></span>
          {f.applied ? <span className="caption text-teal-700">Applied</span> : <button onClick={() => apply(f.id)} data-testid={`apply-fact-${f.id}`} className="btn-ghost btn-sm">Use this</button>}
        </div>
      ))}
    </div>
  );
};

export default function DocumentsPanel({ project, docs, reload }) {
  const { org } = useAuth();
  const [category, setCategory] = useState("other");
  const [busy, setBusy] = useState(false);
  const consent = org?.consent?.accepted;
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/documents`, fd);
      toast.success("Uploaded and encrypted");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const del = async (id) => {
    await api.delete(`/documents/${id}`);
    reload();
  };
  return (
    <div className="space-y-4">
      {!consent && <Notice tone="warn" testid="consent-required-notice">Uploads are disabled until a workspace owner reviews the document handling notice on the <Link to="/app/business" className="underline">Business profile</Link> page.</Notice>}
      <section className="card flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
        <div className="sm:w-56"><label className="label" htmlFor="doc-cat">Document type</label>
          <select id="doc-cat" data-testid="doc-category-select" className="input" value={category} onChange={(e) => setCategory(e.target.value)}>{Object.entries(DOC_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </div>
        <label className={`btn-teal cursor-pointer ${!consent || busy ? "pointer-events-none opacity-50" : ""}`}>
          <Upload className="h-4 w-4" />{busy ? "Encrypting…" : "Upload file"}
          <input type="file" className="sr-only" data-testid="doc-upload-input" disabled={!consent || busy} onChange={upload} accept=".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.xlsx,.pptx" />
        </label>
        <p className="flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" /> Encrypted before storage · max 20 MB · PDF, DOCX and TXT text is scanned for facts</p>
      </section>
      {!docs.length && <Empty title="No documents yet">Pitch decks, drawings, research, accounts and forecasts help complete your applications.</Empty>}
      <ul className="space-y-3">
        {docs.map((d) => (
          <li key={d.id} className="card p-4" data-testid={`document-${d.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><p className="text-sm font-medium">{d.filename}</p><p className="text-xs text-slate-500">{DOC_CATEGORIES[d.category]} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by} · {fmtDateTime(d.created_at)}</p></div>
              <div className="flex gap-2">
                <button aria-label="Download" onClick={() => downloadFile(`/documents/${d.id}/download`, d.filename)} data-testid={`doc-download-${d.id}`} className="btn-ghost btn-sm"><Download className="h-3.5 w-3.5" /></button>
                <button aria-label="Delete" onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`} className="btn-ghost btn-sm text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <Facts doc={d} reload={reload} />
          </li>
        ))}
      </ul>
    </div>
  );
}
